<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useBriefStore } from "@/stores/briefs";
import { api } from "@/services/api";

const store = useBriefStore();
const selectedTaskId = ref<number | null>(null);
const expectedAt = ref("");
const deliveryName = ref("");
const deliverySummary = ref("");
const deliveryUrl = ref("");
const selectedTask = computed(
  () =>
    store.active?.tasks.find((task) => task.id === selectedTaskId.value) ||
    null,
);
const queue = computed(() => store.submitted);
let refreshTimer: number | undefined;
onMounted(() => {
  void store
    .loadList()
    .catch((error) =>
      console.error("[TeamWorkbench] unable to load queue", error),
    );
  refreshTimer = window.setInterval(() => {
    void store.loadList().catch(() => undefined);
    if (store.active) void store.refresh().catch(() => undefined);
  }, 8000);
});
onUnmounted(() => {
  if (refreshTimer) window.clearInterval(refreshTimer);
});
async function selectBrief(id: number) {
  await store.open(id);
  selectedTaskId.value =
    store.active?.tasks.find((task) => task.confirmed)?.id || null;
  expectedAt.value = selectedTask.value?.expected_at || "";
}
async function updateTask(status: string) {
  if (!selectedTask.value) return;
  await api(`/tasks/${selectedTask.value.id}/status`, {
    method: "POST",
    body: JSON.stringify({ status, expectedAt: expectedAt.value }),
  });
  await store.refresh();
  await store.loadList();
}
async function deliver() {
  if (!selectedTask.value) return;
  await api(`/tasks/${selectedTask.value.id}/deliveries`, {
    method: "POST",
    body: JSON.stringify({
      versionName: deliveryName.value,
      summary: deliverySummary.value,
      resultUrl: deliveryUrl.value,
    }),
  });
  deliveryName.value = "";
  deliverySummary.value = "";
  deliveryUrl.value = "";
  await store.refresh();
}
async function acknowledge(id: number) {
  await api(`/changes/${id}/ack`, { method: "POST" });
  await store.refresh();
}
</script>

<template>
  <div class="team">
    <header class="page-head">
      <div>
        <h1>团队工作台</h1>
        <p class="muted">
          客户确认后的 Brief 才进入待办队列，团队接收、执行、交付和反馈。
        </p>
      </div>
    </header>
    <div class="work-layout">
      <aside class="queue">
        <header>
          <h2>待办 Brief</h2>
          <span class="status">{{ queue.length }} 项待处理</span>
        </header>
        <button
          v-for="brief in queue"
          :key="brief.id"
          class="queue__item"
          :class="{ active: store.active?.id === brief.id }"
          @click="selectBrief(brief.id)"
        >
          <span
            ><b>{{ brief.title }}</b
            ><small>{{ brief.client_name }} · {{ brief.status }}</small></span
          ><time>{{
            (brief.submitted_at || brief.updated_at).slice(0, 10)
          }}</time>
        </button>
        <p v-if="!queue.length" class="muted empty">
          暂时没有客户已提交的 Brief
        </p>
      </aside>
      <section class="detail">
        <template v-if="store.active"
          ><header class="detail__head">
            <div>
              <h2>{{ store.active.title }}</h2>
              <p class="muted">{{ store.active.client_name }}</p>
            </div>
          </header>
          <section v-if="store.active.changes.length">
            <h3>客户补充需求</h3>
            <article
              v-for="change in store.active.changes"
              :key="change.id"
              class="change"
            >
              <b>{{ change.summary_zh }}</b>
              <p>原文：{{ change.source_content }}</p>
              <small
                >{{ change.client_name }} ·
                {{ change.created_at.slice(0, 16).replace("T", " ") }}</small
              ><button
                v-if="change.status === 'new'"
                class="btn"
                @click="acknowledge(change.id)"
              >
                确认已阅
              </button>
            </article>
          </section>
          <section>
            <h3>任务</h3>
            <div class="task-tabs">
              <button
                v-for="task in store.active.tasks.filter(
                  (task) => task.confirmed,
                )"
                :key="task.id"
                class="btn"
                :class="{ 'btn--primary': selectedTaskId === task.id }"
                @click="
                  selectedTaskId = task.id;
                  expectedAt = task.expected_at || '';
                "
              >
                {{ task.title }}
              </button>
            </div>
            <div v-if="selectedTask" class="task-detail">
              <p>{{ selectedTask.scope }}</p>
              <p>
                <span
                  v-for="item in selectedTask.deliverables"
                  :key="item"
                  class="chip"
                  >{{ item }}</span
                >
              </p>
              <label
                >任务状态<select
                  class="select"
                  :value="selectedTask.status"
                  @change="
                    updateTask(($event.target as HTMLSelectElement).value)
                  "
                >
                  <option value="submitted">已接收</option>
                  <option value="in_progress">制作中</option>
                </select></label
              ><label
                >预计完成<input
                  v-model="expectedAt"
                  class="input"
                  type="date" /></label
              ><button
                class="btn btn--blue"
                @click="updateTask(selectedTask.status)"
              >
                保存任务进度
              </button>
            </div>
          </section>
          <section v-if="selectedTask">
            <h3>提交交付版本</h3>
            <input
              v-model="deliveryName"
              class="input"
              placeholder="版本名称"
            /><textarea
              v-model="deliverySummary"
              class="textarea"
              placeholder="交付说明"
            /><input
              v-model="deliveryUrl"
              class="input"
              placeholder="交付链接（可选）"
            /><button class="btn btn--primary" @click="deliver">
              提交给客户验收
            </button>
          </section></template
        >
        <p v-else class="empty">从左侧选择一份 Brief 查看任务和变更。</p>
      </section>
    </div>
  </div>
</template>

<style scoped>
.page-head {
  padding: 26px 30px 18px;
  background: #fff;
  border-bottom: 1px solid var(--line);
}
.page-head h1,
.detail h2,
.detail h3 {
  margin: 0;
}
.page-head p {
  margin: 5px 0 0;
}
.work-layout {
  display: grid;
  grid-template-columns: minmax(340px, 460px) minmax(0, 1fr);
  min-height: calc(100vh - 150px);
}
.queue {
  padding: 18px 20px;
  border-right: 1px solid var(--line);
  background: #fbfcfc;
}
.queue header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 13px;
  border-bottom: 1px solid var(--line);
}
.queue h2 {
  margin: 0;
  font-size: 16px;
}
.queue__item {
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 8px;
  padding: 13px;
  border: 1px solid var(--line);
  background: #fff;
  text-align: left;
}
.queue__item.active {
  border-color: #91cfc4;
  background: #effaf7;
  box-shadow: inset 3px 0 var(--success);
}
.queue__item b,
.queue__item small {
  display: block;
}
.queue__item small,
.queue__item time {
  color: var(--muted);
  font-size: 12px;
}
.detail {
  padding: 24px 28px;
  background: #fff;
}
.detail__head {
  padding-bottom: 16px;
  border-bottom: 1px solid var(--line);
}
.detail section {
  display: grid;
  gap: 10px;
  padding: 18px 0;
  border-bottom: 1px solid var(--line);
}
.change {
  padding: 12px;
  border-left: 3px solid var(--brand);
  background: #fff2e9;
}
.change p {
  margin: 4px 0;
}
.change small {
  display: block;
  color: var(--muted);
  margin-bottom: 7px;
}
.task-tabs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.task-detail {
  display: grid;
  gap: 10px;
  padding: 14px;
  border: 1px solid var(--line);
}
.task-detail p {
  margin: 0;
}
.chip {
  display: inline-block;
  padding: 2px 5px;
  margin: 2px;
  background: #f2f5f5;
  color: var(--muted);
  font-size: 12px;
}
.empty {
  padding: 30px;
  text-align: center;
}
@media (max-width: 700px) {
  .page-head {
    padding: 18px 14px;
  }
  .work-layout {
    grid-template-columns: 1fr;
  }
  .queue {
    border-right: 0;
    border-bottom: 1px solid var(--line);
    padding: 14px;
  }
  .detail {
    padding: 18px 14px;
  }
}
</style>
